/**
 * S199 — Mapa unificado de las 3 automatizaciones del producto.
 * Mismos tres motores; un solo lenguaje para el dueño no técnico.
 */
import { NavLink } from 'react-router-dom';
import { Workflow, Zap, Bell, ArrowRight } from 'lucide-react';

const TOOLS = [
  {
    id: 'sequences',
    title: 'Secuencias',
    when: 'Cuando pasa el tiempo o una fecha (cumpleaños, cita, bienvenida…)',
    does: 'Manda emails y WhatsApp en cadena, sola, sin que estés al pendiente.',
    path: '/tenant/marketing/automations',
    icon: Zap,
    accent: '#F72585',
    soft: '#FFF5F9',
    border: 'border-[#F72585]/25',
  },
  {
    id: 'flows',
    title: 'Ruta del lead',
    when: 'Cuando entra un lead (WhatsApp, formulario, manual…)',
    does: 'Dibuja el camino: IA → asignación → llamada → etapa del CRM.',
    path: '/tenant/business/flows',
    icon: Workflow,
    accent: '#059669',
    soft: '#ECFDF5',
    border: 'border-emerald-200',
  },
  {
    id: 'rules',
    title: 'Reglas al instante',
    when: 'En el mismo segundo que pasa algo (lead asignado, etapa, cita…)',
    does: 'Un aviso o acción inmediata al vendedor o al lead. Sin esperas.',
    path: '/tenant/ai/triggers',
    icon: Bell,
    accent: '#7C3AED',
    soft: '#F5F3FF',
    border: 'border-violet-200',
  },
];

/**
 * @param {'sequences'|'flows'|'rules'|null} current — resalta la pantalla actual
 * @param {'full'|'compact'} variant
 */
export default function AutomationsMap({ current = null, variant = 'full' }) {
  const compact = variant === 'compact';

  return (
    <div
      className={`rounded-2xl border border-[#E2E8F0] bg-white ${compact ? 'p-3' : 'p-4'} space-y-3`}
      data-testid="automations-map"
      data-current={current || ''}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className={`${compact ? 'text-sm' : 'text-base'} font-bold text-[#0F172A]`}>
            Tres formas de automatizar (elige según el momento)
          </p>
          {!compact && (
            <p className="text-xs text-[#64748B] mt-1 max-w-2xl">
              No son lo mismo ni se pisan. Usa la que encaje con lo que quieres lograr.
              Los tres motores ya están activos en tu plan según permisos.
            </p>
          )}
        </div>
      </div>

      <div className={`grid gap-2 ${compact ? 'md:grid-cols-3' : 'md:grid-cols-3'}`}>
        {TOOLS.map((tool) => {
          const Icon = tool.icon;
          const active = current === tool.id;
          return (
            <NavLink
              key={tool.id}
              to={tool.path}
              data-testid={`automations-map-${tool.id}`}
              className={`group rounded-xl border ${tool.border} p-3 transition-all hover:shadow-sm ${
                active ? 'ring-2 ring-offset-1' : 'hover:border-[#CBD5E1]'
              }`}
              style={active ? { background: tool.soft, ringColor: tool.accent } : { background: '#fff' }}
            >
              <div className="flex items-start gap-2">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${tool.accent}15` }}
                >
                  <Icon className="w-4 h-4" style={{ color: tool.accent }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-[#0F172A]">{tool.title}</p>
                    {active && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: tool.accent }}
                      >
                        Aquí estás
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-0.5 leading-snug">
                    <span className="font-medium text-[#475569]">Cuándo: </span>
                    {tool.when}
                  </p>
                  {!compact && (
                    <p className="text-[11px] text-[#64748B] mt-1 leading-snug">
                      <span className="font-medium text-[#475569]">Qué hace: </span>
                      {tool.does}
                    </p>
                  )}
                  {!active && (
                    <span
                      className="inline-flex items-center gap-0.5 mt-2 text-[11px] font-semibold group-hover:underline"
                      style={{ color: tool.accent }}
                    >
                      Abrir <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
